import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Download, ExternalLink, Loader2 } from 'lucide-react';
import { generateDoc, ownerToPdf } from '@/lib/generateLiquidacionPdf';
import { MODEL_COLORS, MODEL_LABELS, docLabel, fmt, paymentStatusOf } from '@/lib/liquidaciones';
import { classNames, formatDate } from '@/lib/utils';
import { Owner } from '@/lib/types';

interface OwnerLiquidacionesProps {
  owner: Owner;
  liquidaciones: any[];
  loading: boolean;
}

/**
 * Vista rápida de las liquidaciones de un propietario. Es de solo lectura a
 * propósito: crear, pagar y borrar siguen viviendo en Contabilidad >
 * Liquidaciones, y cada fila enlaza allí con el detalle ya abierto.
 */
export default function OwnerLiquidaciones({ owner, liquidaciones, loading }: OwnerLiquidacionesProps) {
  const navigate = useNavigate();
  const [year, setYear] = useState<number | 'all'>('all');

  const years = useMemo(
    () => [...new Set(liquidaciones.map(l => l.year))].sort((a, b) => b - a),
    [liquidaciones],
  );

  const visible = useMemo(
    () => (year === 'all' ? liquidaciones : liquidaciones.filter(l => l.year === year)),
    [liquidaciones, year],
  );

  const totales = useMemo(() => {
    let total = 0;
    let pendientes = 0;
    let pendienteImporte = 0;
    for (const liq of visible) {
      const saldo = Math.abs(liq.result?.saldo || 0);
      total += saldo;
      if ((liq.paymentStatus || 'none') !== 'completed') {
        pendientes += 1;
        pendienteImporte += saldo;
      }
    }
    return { total, pendientes, pendienteImporte };
  }, [visible]);

  const verEnContabilidad = () => {
    const params = new URLSearchParams({ ownerId: owner.id });
    // Contabilidad no tiene «todos los años»: por defecto abre el año en curso,
    // que puede no ser ninguno de los del propietario. Se le pasa el más
    // reciente que sí tenga para que no aterrice en una lista vacía.
    const destino = year === 'all' ? years[0] : year;
    if (destino) params.set('year', String(destino));
    navigate(`/liquidaciones?${params}`);
  };

  function descargar(liq: any) {
    try {
      // El número de factura se congela al crear la liquidación, así que basta
      // con que lo tenga la liquidación o el propietario.
      if (liq.result?.tipo === 'factura' && !(liq.invoicePrefix || owner.invoicePrefix)) {
        alert('El propietario no tiene configurada la serie de factura (invoicePrefix). Edítalo antes de generar la factura.');
        return;
      }
      generateDoc(ownerToPdf(owner, liq.modelo, liq), liq.result, liq.reservas || [], liq.periodo);
    } catch (err: any) {
      alert(err?.message || 'Error al generar el PDF');
    }
  }

  if (loading) {
    return (
      <div className="card flex items-center justify-center py-12 text-sereno-dark/40">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (liquidaciones.length === 0) {
    return (
      <div className="card text-center py-12">
        <Receipt className="mx-auto mb-3 text-sereno-dark/20" size={40} />
        <p className="text-sm font-medium text-sereno-dark/60">Este propietario no tiene liquidaciones</p>
        <button onClick={verEnContabilidad} className="btn-secondary text-sm mt-4 inline-flex items-center gap-1.5">
          <ExternalLink size={14} /> Crear en Contabilidad
        </button>
      </div>
    );
  }

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
          Liquidaciones ({visible.length})
        </h3>
        <div className="flex items-center gap-2">
          <select
            className="input w-auto py-1.5 text-sm"
            value={year}
            onChange={e => setYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          >
            <option value="all">Todos los años</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={verEnContabilidad}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-sereno-dark/70 hover:bg-sereno-blue/20 hover:text-sereno-dark transition-colors whitespace-nowrap"
            title="Abrir en Contabilidad > Liquidaciones"
          >
            <ExternalLink size={14} /> Contabilidad
          </button>
        </div>
      </div>

      {/* Totales del periodo filtrado. En móvil van en una columna con la
          etiqueta y el importe en la misma línea: en dos columnas, un importe
          de seis cifras parte el símbolo del euro a la línea siguiente. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5 sm:py-3 flex items-baseline justify-between gap-3 sm:block">
          <p className="text-xs text-sereno-dark/50">Liquidaciones</p>
          <p className="text-lg font-bold text-sereno-dark tabular-nums">{visible.length}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5 sm:py-3 flex items-baseline justify-between gap-3 sm:block">
          <p className="text-xs text-sereno-dark/50">Total</p>
          <p className="text-lg font-bold text-green-700 tabular-nums whitespace-nowrap">{fmt(totales.total)}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5 sm:py-3 flex items-baseline justify-between gap-3 sm:block">
          <p className="text-xs text-sereno-dark/50">Pendiente de pago</p>
          <div className="text-right sm:text-left">
            <p className={classNames('text-lg font-bold tabular-nums whitespace-nowrap', totales.pendientes > 0 ? 'text-amber-700' : 'text-sereno-dark/40')}>
              {totales.pendientes > 0 ? fmt(totales.pendienteImporte) : '—'}
            </p>
            {totales.pendientes > 0 && (
              <p className="text-xs text-sereno-dark/40">{totales.pendientes} sin pagar</p>
            )}
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-sereno-dark/40 py-4">Sin liquidaciones en {year}</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-2 font-semibold text-sereno-dark/60">Periodo</th>
                  <th className="text-left py-2 px-2 font-semibold text-sereno-dark/60">Modelo</th>
                  <th className="text-right py-2 px-2 font-semibold text-sereno-dark/60">Reservas</th>
                  <th className="text-right py-2 px-2 font-semibold text-sereno-dark/60">Saldo</th>
                  <th className="text-right py-2 px-2 font-semibold text-sereno-dark/60">Pago</th>
                  <th className="text-right py-2 px-2 font-semibold text-sereno-dark/60">Creada</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map(liq => {
                  const ps = paymentStatusOf(liq);
                  return (
                    <tr
                      key={liq.id}
                      onClick={() => navigate(`/liquidaciones?id=${liq.id}`)}
                      className="border-b border-gray-50 last:border-0 hover:bg-sereno-blue/20 cursor-pointer transition-colors group"
                    >
                      <td className="py-2.5 px-2 font-medium text-sereno-dark whitespace-nowrap">{liq.periodo}</td>
                      <td className="py-2.5 px-2">
                        <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', MODEL_COLORS[liq.modelo] || 'bg-gray-50 text-gray-700 border-gray-200')}>
                          {MODEL_LABELS[liq.modelo] || liq.modelo}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-sereno-dark/70 tabular-nums">{liq.reservas?.length || 0}</td>
                      <td className="py-2.5 px-2 text-right font-semibold text-green-700 tabular-nums whitespace-nowrap">{fmt(Math.abs(liq.result?.saldo || 0))}</td>
                      <td className="py-2.5 px-2 text-right">
                        <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', ps.className)}>{ps.label}</span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-sereno-dark/50 whitespace-nowrap">{formatDate(liq.createdAt)}</td>
                      <td className="py-2.5 px-2 text-right">
                        <button
                          onClick={e => { e.stopPropagation(); descargar(liq); }}
                          disabled={!liq.result}
                          className="p-1.5 rounded-lg text-sereno-dark/30 hover:bg-sereno-blue/30 hover:text-sereno-dark transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={`Descargar ${docLabel(liq)}`}
                        >
                          <Download size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-2.5">
            {visible.map(liq => {
              const ps = paymentStatusOf(liq);
              return (
                <div
                  key={liq.id}
                  onClick={() => navigate(`/liquidaciones?id=${liq.id}`)}
                  className="rounded-xl border border-gray-100 p-3 hover:bg-sereno-blue/20 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-sereno-dark">{liq.periodo}</p>
                      <p className="text-xs text-sereno-dark/50">
                        {liq.reservas?.length || 0} reservas · {formatDate(liq.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="font-bold text-green-700 tabular-nums">{fmt(Math.abs(liq.result?.saldo || 0))}</span>
                      <button
                        onClick={e => { e.stopPropagation(); descargar(liq); }}
                        disabled={!liq.result}
                        className="p-1.5 rounded-lg text-sereno-dark/30 hover:bg-sereno-blue/30 hover:text-sereno-dark transition-colors disabled:opacity-30"
                        title={`Descargar ${docLabel(liq)}`}
                      >
                        <Download size={15} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', MODEL_COLORS[liq.modelo] || 'bg-gray-50 text-gray-700 border-gray-200')}>
                      {MODEL_LABELS[liq.modelo] || liq.modelo}
                    </span>
                    <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border', ps.className)}>{ps.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
